/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AppSession } from 'app/shared/lib/session/session';
import { IApiResults, ApiError, IApiRequest, ApiResponseType, IApiAuthenticationResults, ApiAuthenticationState, IApiOptions, IApiBatch, IApiBatchOptions } from './api-types';
import { ApiAction } from './api-action';
import { ApiQuery } from './api-query';
import { ApiSettings } from 'app/shared/lib/api/api-settings';
import { ApiDownload } from 'app/shared/lib/api/api-download';
import { IApiSessionState } from 'app/shared/lib/api/api-session-types';
import { mapApiTenant, mapApiLocation, mapApiUser, mapApiUserContext, mapApiApplication } from 'app/shared/lib/api/api-session';
import { SignOnCommand } from 'app/core/pages/signon/signon-base';
import { ApiStoredObject } from 'app/shared/lib/api/api-stored-objects';
import { sleep } from 'app/shared/lib/utilities';
import { ApiBatch } from 'app/shared/lib/api/api-batch';
import { SessionChangeEventKey } from 'app/shared/lib/session/session-types';

export function apiCall(target: any, methodName: string, descriptor: TypedPropertyDescriptor<any>): TypedPropertyDescriptor<any> {
    return descriptor;
}

function trimQueryString(url: string): string {
    const questionIndex = url.indexOf('?');
    return questionIndex >= 0 ? `${url.substring(0, questionIndex)}?...` : url;
}

@autoinject
export class Api {

    private static defaultOptions: IApiOptions = {
        logErrorResults: true,
        authenticate: true,
        skipRefresh: false,
        applicationEventCode: null
    };

    public readonly baseUrl: string;

    private apiMessage: string;
    private pendingPromise: Promise<any> | null = null;
    private twoFactorRequired: boolean = false;

    constructor(
        private readonly session: AppSession,
        private readonly env: EnvironmentService,
        private readonly logger: AppLogger) {

        this.logger.source = this;
        this.logger.target = 'console';
        this.baseUrl = this.env.resolveUrl(env.config.essetteApi.baseUrl);

        this.apiMessage = `HMS Essette Application Client API - ${this.env.config.description} [${this.env.config.instanceScope}]`;
    }

    public async logIn(loginName: string, password: string): Promise<IApiAuthenticationResults> {
        return await this.queueAction(() => this.logInInternal(loginName, password));
    }

    public async signOn(signOnCommand: SignOnCommand): Promise<IApiAuthenticationResults> {
        return await this.queueAction(() => this.signOnInternal(signOnCommand));
    }

    public async logOut(): Promise<ApiAuthenticationState> {
        return await this.queueAction(() => this.logOutInternal());
    }

    public async checkIn(authenticationCode: string = '', trustClient: boolean = false): Promise<ApiAuthenticationState> {
        return await this.queueAction(() => this.checkinInternal(authenticationCode, trustClient));
    }

    public async verifyLogin(): Promise<string> {
        return await this.queueAction(() => this.verifyLoginInternal());
    }

    public async lockSession(): Promise<ApiAuthenticationState> {
        return await this.queueAction(() => this.lockSessionInternal(true));
    }

    public async unlockSession(loginName: string, password: string): Promise<ApiAuthenticationState> {
        return await this.queueAction(() => this.lockSessionInternal(false, loginName, password));
    }

    public async refreshSession(): Promise<ApiAuthenticationState> {
        return await this.queueAction(() => this.refreshSessionInternal());
    }

    public async acceptInvitation(invitationCode: string, loginName: string, password: string, firstName?: string, lastName?: string): Promise<IApiAuthenticationResults> {
        return await this.queueAction(() => this.acceptInvitationInternal(invitationCode, loginName, password, firstName, lastName));
    }

    private async logInInternal(loginName: string, password: string): Promise<IApiAuthenticationResults> {
        const clientToken = this.session.isClientTrusted() ? this.session.clientToken : '';
        const command: any = {
            credentials: {
                loginName: loginName,
                password: password
            },
            token: clientToken ? { client: { value: clientToken } } : undefined,
            application: { code: this.env.config.application },
            message: this.apiMessage
        };

        const remember = this.session.getRememberedState();
        if(remember.enabled) {
            this.logger.info(`Attempting to Use Remember Check-In: Tenant: '${remember.tenant}', Location: '${remember.location}', Context: ${remember.context}.`);
            if(remember.tenant) {
                command.tenant = { code: remember.tenant };
            }
            if(remember.location) {
                command.location = { code: remember.location };
            }
            if(remember.context) {
                command.user = { context: { id: remember.context } };
            }
        }
        try {
            const action = new ApiAction(null, 'authentication/logIn', command).withTenant('system');
            const result = await this.post(action, { authenticate: false }) as IApiAuthenticationResults;

            this.updateSessionTokens(result, true, true);
            const session = result.session;
            this.session.updateLocked(false);
            this.session.updateAuthenticationCode('');
            this.updateSession(session || null);
            this.session.save('logIn');

            return result;
        }
        catch(error) {
            this.logger.error('API Log In Failed.', error);
            throw error;
        }

    }

    private async signOnInternal(command: SignOnCommand): Promise<IApiAuthenticationResults> {
        try {
            const action = new ApiAction(null, 'authentication/signOn', command).withTenant('system');
            const result = await this.post(action, { authenticate: false }) as IApiAuthenticationResults;

            this.updateSessionTokens(result, true, true);
            const session = result.session;
            this.session.updateLocked(false);
            this.session.updateAuthenticationCode('');
            this.updateSession(session || null);
            this.session.save('logIn');

            return result;
        }
        catch(error) {
            this.logger.error('API Log In Failed.', error);
            throw error;
        }

    }

    private async verifyLoginInternal(): Promise<string> {
        const clientToken = this.session.isClientTrusted() ? this.session.clientToken : '';
        const command = {
            tenant: { code: this.session.tenant },
            token: clientToken ? { client: { value: clientToken } } : undefined,
            message: this.apiMessage
        };
        try {
            const action = new ApiAction(null, 'authentication/login/verify', command).withTenant('system');
            const result = await this.post(action, { authenticate: true, skipRefresh: true });
            this.twoFactorRequired = result?.state === 'twoFactorRequired';

            const code = result?.credentials?.code || '';
            this.session.updateAuthenticationCode(code);
            return code; // return authentication code
        }
        catch(error) {
            this.logger.error('API Verify Login Failed.', error);
            throw error;
        }
    }

    private async logOutInternal(sessionEventKey: SessionChangeEventKey = 'logOut'): Promise<ApiAuthenticationState> {

        const command = {
            token: { refresh: { value: this.session.refreshToken } },
            message: this.apiMessage
        };
        try {
            const action = new ApiAction(null, 'authentication/logOut', command).withTenant('system');
            const result = await this.post(action, { authenticate: false, skipRefresh: true });
            this.session.clear(sessionEventKey);
            this.session.configureEnvironment();

            return result.state;
        }
        catch(error) {
            this.logger.error('API Log Out Failed.', error);
            throw error;
        }

    }

    private async checkinInternal(authenticationCode: string = '', trustClient: boolean = false): Promise<ApiAuthenticationState> {

        const command = {
            tenant: { code: this.session.tenant },
            location: { code: this.session.location },
            user: { context: { id: this.session.context } },
            credentials: authenticationCode ? { code: authenticationCode } : undefined,
            message: this.apiMessage
        };
        try {
            const action = new ApiAction(null, 'authentication/checkIn', command).withTenant('system');
            const result = await this.post(action, { authenticate: true, skipRefresh: true });

            this.updateSessionTokens(result, true, true, trustClient); // remember 'client' token for 2FA if 'trustClient' is true
            this.session.updateAuthenticationCode(authenticationCode);
            this.session.updateLocked(false);
            this.updateSession(result.session);

            this.session.save('checkIn');

            return result.state;
        }
        catch(error) {
            this.logger.error('API Check In Failed.', error);
            throw error;
        }

    }

    private async lockSessionInternal(isLocked: boolean, loginName?: string, password?: string): Promise<ApiAuthenticationState> {

        if(!this.session.isLoggedIn()) {
            throw new ApiError('Cannot Lock/Unlock Session: Not Logged In');
        }

        const command = {
            isLocked,
            credentials: isLocked ? undefined : { loginName: loginName, password: password },
            token: { refresh: { value: this.session.refreshToken } },
            message: this.apiMessage
        };
        try {
            const action = new ApiAction(null, 'authentication/lock', command).withTenant('system');
            const result = await this.post(action, { authenticate: false, skipRefresh: true });

            this.updateSessionTokens(result, true, false);
            const sessionLocked = result.token.isLocked || false;
            this.session.updateLocked(sessionLocked);

            this.updateSession(result.session);

            this.session.save(sessionLocked ? 'lock' : 'unlock');
            this.session.configureEnvironment();

            return result.state;
        }
        catch(error) {
            this.logger.error(`API Session ${isLocked ? 'Lock' : 'Unlock'} Failed.`, error);
            throw error;
        }

    }

    private async refreshSessionInternal(): Promise<ApiAuthenticationState> {

        if(!this.session.isLoggedIn()) {
            throw new ApiError('Cannot Refresh Session: Not Logged In');
        }

        const command = {
            token: { refresh: { value: this.session.refreshToken } },
            message: this.apiMessage
        };
        try {
            const action = new ApiAction(null, 'authentication/refresh', command).withTenant('system');
            const result = await this.post(action, { authenticate: false, skipRefresh: true });

            this.updateSessionTokens(result, true, false);
            this.updateSession(result.session);

            this.session.save('refresh');

            // If 2FA required, skip this step as user is not yet authenticated.
            if(!result?.session?.user && !this.twoFactorRequired) {
                // If user is null, then login is invalid. Set session to expire.
                const refresh = result.token.refresh;
                if(refresh) {
                    this.session.updateRefreshToken(
                        (refresh.value || '') as string, 1);
                }
            }

            return result.state;
        }
        catch(error) {
            this.logger.error('API Session Refresh Failed.', error);
            throw error;
        }

    }

    private async acceptInvitationInternal(invitationCode: string, loginName: string, password: string, firstName?: string, lastName?: string): Promise<IApiAuthenticationResults> {
        const useExisting = !(firstName && lastName);
        const command = {
            credentials: {
                code: invitationCode,
                loginName: loginName,
                password: password
            },
            user: useExisting ? undefined : {
                name: {
                    first: firstName,
                    last: lastName
                }
            }
        };

        try {
            const action = new ApiAction(null, 'authentication/invitation/accept', command).withTenant('system');
            const result = await this.post(action, { authenticate: false }) as IApiAuthenticationResults;

            return result;
        } catch(error) {
            this.logger.error('API Accept Invitation Failed', error);
            throw error;
        }
    }

    private updateSessionTokens(result: any, checkAccess: boolean, checkRefresh: boolean, checkClient: boolean = false): void {
        if(!result || !result.token) { return; }
        if(checkAccess) {
            const access = result.token.access;
            if(access) {
                this.session.updateAccessToken(
                    (access.value || '') as string,
                    (access.expirationSeconds || 0) as number);
            }
            else {
                this.session.updateAccessToken('', 0);
            }
        }
        if(checkRefresh) {
            const refresh = result.token.refresh;
            if(refresh) {
                this.session.updateRefreshToken(
                    (refresh.value || '') as string,
                    (refresh.expirationSeconds || 0) as number);
            }
            else {
                this.session.updateRefreshToken('', 0);
            }
        }
        if(checkClient) {
            const client = result.token.client;
            if(client) {
                this.session.updateClientToken(
                    (client.value || '') as string,
                    (client.expirationSeconds || 0) as number);
            }
            else {
                this.session.updateClientToken('', 0);
            }
        }
    }

    private updateSession(data: IApiSessionState | null): void {
        if(!data) {
            this.session.updateApplication(null);
            this.session.updateTenant(null);
            this.session.updateUser(null);
            this.session.updateUserContext(null);
            this.session.updateLocation(null);
            this.session.updateLocked(false);
            this.session.configureEnvironment();
            return;
        }

        this.session.updateApplication(mapApiApplication(data.application || null));
        this.session.updateTenant(mapApiTenant(data.tenant || null));
        this.session.updateUser(mapApiUser(data.user || null));
        this.session.updateUserContext(mapApiUserContext(data.user && data.user.context || null));
        this.session.updateLocation(mapApiLocation(data.location || null));

        // [Nick] Filter out tenants that don't have any valid user contexts associated
        const availableTenants = (data.availableTenants || []).filter((t) => {
            return (t.userContexts || []).some((uc) => { return uc && !!uc.id; });
        });
        this.session.updateAvailableTenants(
            availableTenants.map(t => mapApiTenant(t)!));
        this.session.configureEnvironment();
    }

    private async queueAction<T>(f: () => Promise<T>): Promise<T> {
        try {
            const promise = f();
            this.pendingPromise = promise;
            return await promise;
        }
        finally {
            this.pendingPromise = null;
        }
    }

    public query(url: string): ApiQuery {
        return new ApiQuery(url);
    }

    public settings(url: string): ApiSettings {
        return new ApiSettings(url);
    }

    public action(name: string | null, url: string, body: Object): ApiAction {
        return new ApiAction(name, url, body);
    }

    public download(url: string): ApiDownload {
        return new ApiDownload(url);
    }

    public storedObject(store: string, url: string, body?: Object): ApiStoredObject {
        return new ApiStoredObject(store, url, body);
    }

    public async get(query: IApiRequest, options?: Partial<IApiOptions>): Promise<any> {

        const appliedOptions = { ...Api.defaultOptions, ...options };

        if(!query.hasTenant() && this.session.tenant) {
            query.withTenant(this.session.tenant);
        }

        const url = query.toString();
        this.logger.debug(`HTTP GET ${url}`, { query, options });

        await this.prepareSession(url, appliedOptions);

        return await this.send('GET', url, null, appliedOptions, query.responseType);
    }

    public async post(action: IApiRequest, options?: Partial<IApiOptions>): Promise<any> {

        const appliedOptions = { ...Api.defaultOptions, ...options };

        if(!action.hasTenant() && this.session.tenant) {
            action.withTenant(this.session.tenant);
        }

        const url = action.toString();
        this.logger.debug(`HTTP POST ${url}`, { action, options });

        await this.prepareSession(url, appliedOptions);

        return await this.send('POST', url, action.body, appliedOptions, action.responseType);
    }

    public async put(action: IApiRequest, options?: Partial<IApiOptions>): Promise<any> {

        const appliedOptions = { ...Api.defaultOptions, ...options };

        if(!action.hasTenant() && this.session.tenant) {
            action.withTenant(this.session.tenant);
        }

        const url = action.toString();
        this.logger.debug(`HTTP PUT ${url}`, { action, options });

        await this.prepareSession(url, appliedOptions);

        return await this.send('PUT', url, action.body, appliedOptions, action.responseType);
    }

    public async delete(action: IApiRequest, options?: Partial<IApiOptions>): Promise<any> {

        const appliedOptions = { ...Api.defaultOptions, ...options };

        if(!action.hasTenant() && this.session.tenant) {
            action.withTenant(this.session.tenant);
        }

        const url = action.toString();
        this.logger.debug(`HTTP DELETE ${url}`, { action, options });

        await this.prepareSession(url, appliedOptions);

        return await this.send('DELETE', url, action.body, appliedOptions, action.responseType);
    }

    public batch(): ApiBatch {
        return new ApiBatch();
    }

    public execute(batch: IApiBatch, options?: Partial<IApiBatchOptions>): Promise<any[]> {
        const appliedOptions = { ...Api.defaultOptions, ...options };
        return batch.execute(this, appliedOptions);
    }

    protected async prepareSession(url: string, options: IApiOptions): Promise<void> {
        if(options.authenticate && !this.session.isValid()) {

            this.logger.debug('Preparing Session...', { url });

            while(this.pendingPromise != null) {
                this.logger.debug('Awaiting Existing Authentication...', { url });
                await this.pendingPromise;
            }

            if(this.session.isValid()) {
                this.logger.debug('Existing Authentication Completed...', { url });
                return;
            }

            // only refresh if the API call requires authentication
            if(options.authenticate && !options.skipRefresh && this.session.isLoggedIn()) {
                this.logger.debug('Refreshing Session...', { url });
                this.logger.debug('Session State', this.session);

                // in case another window has refreshed the session
                let doubleCheck: boolean = false;
                try {
                    await this.refreshSession();
                }
                catch(refreshError) {
                    this.logger.warn('Error Refreshing Session', refreshError);
                    doubleCheck = true;
                }
                if(doubleCheck) {
                    await sleep(500);
                    if(this.session.isLoggedIn()) {
                        // nothing to see here, move along
                        this.logger.debug('Session Refreshed Elsewhere...', { url });
                        this.logger.debug('Session State', this.session);
                        return;
                    }
                }
            }

            if(!this.session.isLoggedIn()) {
                throw new ApiError('Unable to Authenticate with API', undefined, undefined, url);
            }
        }

    }

    private tryParseJson(json: string): any {
        try {
            return JSON.parse(json);
        }
        catch(error) {
            this.logger.warn('Unable to parse JSON response', error);
        }
    }

    private send(method: string, relativeUrl: string, body: object | null, options: IApiOptions, responseType: ApiResponseType = 'json'): Promise<any> {

        return new Promise<any>((resolve, reject) => {

            if(options.authenticate && this.session.isLocked()) {
                // if session is locked throw error
                reject(new ApiError(`API Request Error: Session Locked`, undefined, method, relativeUrl));
                return;
            }

            const request = new XMLHttpRequest();
            request.onload = () => {
                this.logger.debug(`HTTP ${method} ${relativeUrl} => ${request.status} ${request.statusText}`);
                if(request.status >= 200 && request.status < 300) {
                    if(responseType === 'json') {
                        const results = this.tryParseJson(request.response);
                        if(options.logErrorResults) {
                            this.logErrors(request, method, relativeUrl, results);
                        }
                        resolve(results);
                    }
                    else {
                        resolve(request.response);
                    }
                }
                else {
                    if(responseType === 'json') {
                        const results = this.tryParseJson(request.response);
                        if(options.logErrorResults) {
                            this.logErrors(request, method, relativeUrl, results, true);
                        }
                        reject(new ApiError(`API Request Error: ${request.status} ${request.statusText}`, request.status, method, relativeUrl, results && results.status || undefined));

                        // need to account for instances when a user's permissions are changed while they are logged in
                        if(request.status == 401) { // Error 401 - Authorization Failed
                            this.forceReauth();
                        }
                    }
                    else {
                        reject(new ApiError(`API Request Error: ${request.status} ${request.statusText}`, request.status, method, relativeUrl));
                    }
                }
            };
            request.onerror = () => {
                reject(new ApiError(`API Request Error: ${request.status} ${request.statusText}`, request.status, method, relativeUrl));
            };
            request.open(method, this.baseUrl + relativeUrl, true);
            request.setRequestHeader('Content-Type', 'application/json');
            request.withCredentials = true;
            if(options.authenticate && this.session.isLoggedIn()) {
                request.setRequestHeader('Authorization', `EssetteWebApi ${this.session.accessToken}`);
            }
            if(responseType === 'blob') {
                request.responseType = 'blob';
            }
            if(body !== undefined && body !== null) {
                request.send(JSON.stringify(body));
            }
            else {
                request.send();
            }
        });

    }

    public getCollectionCount(parentItem: any, property: string): number {
        const collections: any = parentItem && parentItem.$collections ? parentItem.$collections : {};
        const entry = collections[property];
        return entry && entry.count !== undefined && entry.count !== null ? entry.count as number : 0;
    }

    private logErrors(request: XMLHttpRequest, method: string, relativeUrl: string, results?: IApiResults, error: boolean = false): void {
        const table: any[] = [];
        let hasErrors: boolean = error;
        let hasWarnings: boolean = false;
        if(results && results.status) {
            if(results.status.errors) {
                for(const error of results.status.errors) {
                    table.push({ description: error.description, code: error.code, property: error.property, level: 'error' });
                    hasErrors = true;
                }
            }
            if(results.status.warnings) {
                for(const warning of results.status.warnings) {
                    table.push({ description: warning.description, code: warning.code, property: warning.property, level: 'warn' });
                    hasWarnings = true;
                }
            }
        }
        const type = (hasErrors && hasWarnings) ? 'error(s)/warning(s)' : (hasErrors ? 'error(s)' : 'warning(s)');
        const message = `HTTP ${request.status} ${request.statusText}: ${table.length} ${type} [${method} ${trimQueryString(relativeUrl)}]`
        const details = { status: request.status, statusText: request.statusText, method, relativeUrl, table };
        if(hasErrors) {
            this.logger.error(message, details);
        }
        else if(hasWarnings) {
            this.logger.warn(message, details);
        }
    }

    public async forceReauth(): Promise<ApiAuthenticationState> {
        return await this.queueAction(() => this.logOutInternal('authFailed'));
    }

    //
    // Mock API Methods //
    //

    public async mockGet(responseFile: string): Promise<any> {
        return fetch('./mock/' + responseFile + '.json').then((response) => response.json());
    }

    public async mockPost(): Promise<any> {
        return { isSuccessful: true };
    }

    public async mockDelete(): Promise<any> {
        return { isSuccessful: true };
    }

}

